import React, { useState, useCallback, useEffect } from 'react';
import { useField } from 'formik';
import { propOr, propEq } from 'ramda';
import PropTypes from 'prop-types';

import {
  ID,
  RIGHT,
  LEFT,
  STATUS,
  EXAMINATION_STATUS,
  FUNDUS_IMAGES,
  EYE_EXTERIOR_IMAGES,
  useWebsocketActions,
} from 'store/examinations';
import { EXAMINATION_REAL_TIME, usePermissions } from 'permissions';
import QuestionContainer from 'components/QuestionContainer';
import Button from 'components/Button';
import Message from 'components/Message';

import DeleteSection from './DeleteSection';

const Submitting = ({ examinationId, examinationStatus, readMode }) => {
  const can = usePermissions();
  const [offlineWarn, setOfflineWarn] = useState(false);
  const { updateExaminationProgress } = useWebsocketActions(can.update(EXAMINATION_REAL_TIME));
  const [{ value: right }] = useField(RIGHT);
  const [{ value: left }] = useField(LEFT);

  const checkOfflineFiles = useCallback(
    () =>
      [right, left]
        .reduce((acc, item) => [...acc, ...propOr([], EYE_EXTERIOR_IMAGES, item), ...propOr([], FUNDUS_IMAGES, item)], [])
        .some(propEq(true, 'offline')),
    [right, left]
  );
  const setStatus = useCallback(
    (status) => {
      const hasOfflineFiles = checkOfflineFiles();

      if (hasOfflineFiles) {
        setOfflineWarn(true);
      } else {
        updateExaminationProgress({ [ID]: examinationId, [STATUS]: status });
      }
    },
    [examinationId, updateExaminationProgress, checkOfflineFiles]
  );
  const handleSubmit = useCallback(() => setStatus(EXAMINATION_STATUS.SUBMITTED), [setStatus]);

  useEffect(() => {
    setOfflineWarn(($) => ($ ? !$ : $));
  }, [examinationId, readMode, right, left]);

  if (readMode) return null;

  return (
    <>
      <QuestionContainer label="Freigeben">
        <Button type="button" onClick={handleSubmit} color="success">
          Zum Befunden freigeben
        </Button>
        {offlineWarn && (
          <Message type="error">
            Der Untersuch kann nicht übermittelt werden, solange nicht alle Bilder vom Offline-Modus hochgeladen wurden.
          </Message>
        )}
      </QuestionContainer>
      {examinationStatus === EXAMINATION_STATUS.IN_PREPARATION && <DeleteSection examId={examinationId} />}
    </>
  );
};

Submitting.propTypes = {
  examinationId: PropTypes.string.isRequired,
  examinationStatus: PropTypes.number.isRequired,
  readMode: PropTypes.bool.isRequired,
};

export default Submitting;
